/**
 * Whose a key press is: the page's shortcuts (the backtick for Owner tools,
 * the block popup's arrows) never take a key from a field it's typed into,
 * or from a list that's open.
 */

// Inputs that take no typing: the key isn't theirs.
const NOT_TYPED = new Set(["button", "checkbox", "color", "file", "image", "radio", "range", "reset", "submit"]);

/** Whether a key pressed with focus here would be typed into something. */
export function isTypingIn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    return true;
  }
  // The attribute too, for engines without isContentEditable (jsdom).
  if (target.isContentEditable || target.closest("[contenteditable]:not([contenteditable='false'])")) {
    return true;
  }
  return target instanceof HTMLInputElement && !NOT_TYPED.has(target.type);
}

/** A combobox whose list is open: Escape there is the list's (Combobox). */
export function isOpenList(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    target.getAttribute("role") === "combobox" &&
    target.getAttribute("aria-expanded") === "true"
  );
}

// Roles whose own keyboard use includes the arrow keys.
const ARROW_ROLES = new Set([
  "combobox",
  "grid",
  "listbox",
  "menu",
  "menubar",
  "menuitem",
  "option",
  "radio",
  "radiogroup",
  "slider",
  "spinbutton",
  "tab",
  "tablist",
  "tree",
  "treegrid",
]);

/**
 * Whether ← and → do something of their own with focus here: move the caret
 * in a field, pick in a list, a radio group or a slider. Any input at all,
 * so a radio button or a range keeps its arrows too.
 */
export function takesArrowKeys(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target instanceof HTMLInputElement || isTypingIn(target)) {
    return true;
  }
  return ARROW_ROLES.has(target.getAttribute("role") ?? "");
}
